import { useState, useRef, useEffect } from "react";
import {
  BuilderBlock,
  BuilderPage,
  BlockType,
  createHeroBlock,
  createStoryBlock,
  createApplicationsBlock,
  createCertificationsBlock,
  createCTABlock,
  createSpecsGridBlock,
  createGalleryBlock,
  createVideoBlock,
} from "./types";
import BlockPreview from "./BlockPreview";
import BlockEditor from "./BlockEditor";
import BuilderPreviewModal from "./BuilderPreviewModal";

interface Props {
  lang: "vi" | "kr";
  pages: BuilderPage[];
  productName?: string;
  onChange: (pages: BuilderPage[]) => void;
}

const BLOCK_LIBRARY: { type: BlockType; label: string; icon: string; description: string; color: string }[] = [
  { type: "hero", label: "Hero Banner", icon: "ri-image-2-line", description: "Ảnh nền lớn + tiêu đề + CTA", color: "bg-rose-50 text-rose-600 border-rose-200" },
  { type: "story", label: "Product Story", icon: "ri-layout-column-line", description: "Zigzag ảnh + nội dung", color: "bg-[#EDF2FB] text-[#004A99] border-[#004A99]/20" },
  { type: "applications", label: "Ứng dụng", icon: "ri-apps-line", description: "Grid ứng dụng thực tế", color: "bg-emerald-50 text-emerald-600 border-emerald-200" },
  { type: "certifications", label: "Chứng nhận", icon: "ri-award-line", description: "Bằng sáng chế & ISO", color: "bg-amber-50 text-amber-600 border-amber-200" },
  { type: "cta", label: "CTA / Liên hệ", icon: "ri-mail-send-line", description: "Form báo giá & liên hệ", color: "bg-violet-50 text-violet-600 border-violet-200" },
  { type: "specs_grid", label: "Thông số", icon: "ri-table-line", description: "Grid thông số kỹ thuật", color: "bg-cyan-50 text-cyan-600 border-cyan-200" },
  { type: "gallery", label: "Thư viện ảnh", icon: "ri-gallery-line", description: "Grid / Masonry ảnh", color: "bg-orange-50 text-orange-600 border-orange-200" },
  { type: "video", label: "Video", icon: "ri-video-line", description: "Video nhúng với controls", color: "bg-red-50 text-red-600 border-red-200" },
];

const createBlock = (type: BlockType): BuilderBlock => {
  const id = `block-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`;
  switch (type) {
    case "hero": return { id, data: createHeroBlock() };
    case "story": return { id, data: createStoryBlock() };
    case "applications": return { id, data: createApplicationsBlock() };
    case "certifications": return { id, data: createCertificationsBlock() };
    case "cta": return { id, data: createCTABlock() };
    case "specs_grid": return { id, data: createSpecsGridBlock() };
    case "gallery": return { id, data: createGalleryBlock() };
    case "video": return { id, data: createVideoBlock() };
    default: return { id, data: createStoryBlock() };
  }
};

const VisualBuilder: React.FC<Props> = ({ lang, pages, productName, onChange }) => {
  const t = (vi: string, kr: string) => lang === "vi" ? vi : kr;

  // FIX: Use ref to track active page index stably
  const [activePageIdx, setActivePageIdx] = useState(0);
  const activePageIdxRef = useRef(0);

  const [selectedBlockId, setSelectedBlockId] = useState<string | null>(null);
  const [showLibrary, setShowLibrary] = useState(false);
  const [editPanelOpen, setEditPanelOpen] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const dragBlockId = useRef<string | null>(null);
  const dragOverBlockId = useRef<string | null>(null);
  const canvasRef = useRef<HTMLDivElement>(null);

  // Ensure at least one page
  const safePages = pages.length > 0 ? pages : [{ id: "page-1", name: "Trang 1", blocks: [] }];

  // FIX: Clamp activePageIdx when pages shrink
  const clampedIdx = Math.min(activePageIdx, safePages.length - 1);
  const activePage = safePages[clampedIdx] ?? safePages[0];

  // Sync ref
  useEffect(() => {
    activePageIdxRef.current = clampedIdx;
  }, [clampedIdx]);

  const updatePage = (updated: BuilderPage) => {
    const next = safePages.map((p, i) => i === clampedIdx ? updated : p);
    onChange(next);
  };

  const addPage = () => {
    const newPage: BuilderPage = {
      id: `page-${Date.now()}`,
      name: `Trang ${safePages.length + 1}`,
      blocks: [],
    };
    const newPages = [...safePages, newPage];
    onChange(newPages);
    // FIX: Set index AFTER onChange so it points to the new page correctly
    const newIdx = newPages.length - 1;
    setActivePageIdx(newIdx);
    activePageIdxRef.current = newIdx;
    setSelectedBlockId(null);
    setEditPanelOpen(false);
  };

  const deletePage = (idx: number) => {
    if (safePages.length <= 1) return;
    const next = safePages.filter((_, i) => i !== idx);
    onChange(next);
    const newIdx = Math.max(0, idx - 1);
    setActivePageIdx(newIdx);
    activePageIdxRef.current = newIdx;
    setSelectedBlockId(null);
    setEditPanelOpen(false);
  };

  const duplicatePage = (idx: number) => {
    const src = safePages[idx];
    const copy: BuilderPage = {
      id: `page-${Date.now()}`,
      name: `${src.name} (copy)`,
      blocks: src.blocks.map((b) => ({ ...b, id: `block-${Date.now()}-${Math.random().toString(36).slice(2, 6)}` })),
    };
    const next = [...safePages.slice(0, idx + 1), copy, ...safePages.slice(idx + 1)];
    onChange(next);
    const newIdx = idx + 1;
    setActivePageIdx(newIdx);
    activePageIdxRef.current = newIdx;
  };

  const renamePage = (idx: number, name: string) => {
    const next = safePages.map((p, i) => i === idx ? { ...p, name } : p);
    onChange(next);
  };

  const switchPage = (idx: number) => {
    setActivePageIdx(idx);
    activePageIdxRef.current = idx;
    setSelectedBlockId(null);
    setEditPanelOpen(false);
    setShowLibrary(false);
    // Scroll canvas to top
    if (canvasRef.current) {
      canvasRef.current.scrollTop = 0;
    }
  };

  const addBlock = (type: BlockType) => {
    const block = createBlock(type);
    updatePage({ ...activePage, blocks: [...activePage.blocks, block] });
    setSelectedBlockId(block.id);
    setShowLibrary(false);
    setEditPanelOpen(true);
    // Scroll to bottom after adding
    setTimeout(() => {
      if (canvasRef.current) {
        canvasRef.current.scrollTop = canvasRef.current.scrollHeight;
      }
    }, 100);
  };

  const updateBlock = (updated: BuilderBlock) => {
    const blocks = activePage.blocks.map((b) => b.id === updated.id ? updated : b);
    updatePage({ ...activePage, blocks });
  };

  const deleteBlock = (id: string) => {
    const blocks = activePage.blocks.filter((b) => b.id !== id);
    updatePage({ ...activePage, blocks });
    if (selectedBlockId === id) {
      setSelectedBlockId(null);
      setEditPanelOpen(false);
    }
  };

  const duplicateBlock = (id: string) => {
    const src = activePage.blocks.find((b) => b.id === id);
    if (!src) return;
    const copy: BuilderBlock = {
      id: `block-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
      data: JSON.parse(JSON.stringify(src.data)),
    };
    const idx = activePage.blocks.findIndex((b) => b.id === id);
    const blocks = [...activePage.blocks.slice(0, idx + 1), copy, ...activePage.blocks.slice(idx + 1)];
    updatePage({ ...activePage, blocks });
    setSelectedBlockId(copy.id);
  };

  const moveBlock = (id: string, dir: "up" | "down") => {
    const idx = activePage.blocks.findIndex((b) => b.id === id);
    if (idx === -1) return;
    if (dir === "up" && idx === 0) return;
    if (dir === "down" && idx === activePage.blocks.length - 1) return;
    const blocks = [...activePage.blocks];
    const swapIdx = dir === "up" ? idx - 1 : idx + 1;
    [blocks[idx], blocks[swapIdx]] = [blocks[swapIdx], blocks[idx]];
    updatePage({ ...activePage, blocks });
  };

  const handleDragStart = (id: string) => { dragBlockId.current = id; };
  const handleDragOver = (e: React.DragEvent, id: string) => {
    e.preventDefault();
    dragOverBlockId.current = id;
  };
  const handleDrop = () => {
    if (!dragBlockId.current || !dragOverBlockId.current || dragBlockId.current === dragOverBlockId.current) return;
    const blocks = [...activePage.blocks];
    const fromIdx = blocks.findIndex((b) => b.id === dragBlockId.current);
    const toIdx = blocks.findIndex((b) => b.id === dragOverBlockId.current);
    if (fromIdx === -1 || toIdx === -1) return;
    const [moved] = blocks.splice(fromIdx, 1);
    blocks.splice(toIdx, 0, moved);
    updatePage({ ...activePage, blocks });
    dragBlockId.current = null;
    dragOverBlockId.current = null;
  };

  const selectedBlock = activePage.blocks.find((b) => b.id === selectedBlockId) ?? null;

  return (
    <>
      {showPreview && (
        <BuilderPreviewModal
          pages={safePages}
          productName={productName}
          onClose={() => setShowPreview(false)}
        />
      )}
      <div className="flex flex-col" style={{ height: "100%", minHeight: 0 }}>
        {/* ── Top bar ── */}
        <div className="flex items-center justify-between px-4 py-3 bg-[#F8F9FA] border-b border-gray-200 flex-shrink-0">
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 flex items-center justify-center bg-[#004A99] rounded-lg">
              <i className="ri-layout-masonry-line text-white text-sm" />
            </div>
            <div>
              <p className="text-xs font-bold text-gray-800">{t("Visual Builder", "비주얼 빌더")}</p>
              <p className="text-[10px] text-gray-400">{activePage.blocks.length} {t("blocks", "블록")} · {safePages.length} {t("trang", "페이지")}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setShowPreview(true)}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-semibold rounded-lg cursor-pointer whitespace-nowrap transition-colors"
            >
              <i className="ri-eye-line" />
              {t("Preview", "미리보기")}
            </button>
            <button
              type="button"
              onClick={() => { setShowLibrary(!showLibrary); setEditPanelOpen(false); }}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-[#004A99] text-white text-xs font-semibold rounded-lg cursor-pointer whitespace-nowrap hover:bg-[#003a7a] transition-colors"
            >
              <i className="ri-add-line" />
              {t("Thêm block", "블록 추가")}
            </button>
            {selectedBlock && (
              <button
                type="button"
                onClick={() => setEditPanelOpen(!editPanelOpen)}
                className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-lg cursor-pointer whitespace-nowrap transition-colors ${editPanelOpen ? "bg-gray-800 text-white" : "bg-white border border-gray-200 text-gray-700 hover:border-gray-300"}`}
              >
                <i className="ri-settings-3-line" />
                {t("Chỉnh sửa", "편집")}
              </button>
            )}
          </div>
        </div>

        {/* ── Page tabs ── */}
        <div className="flex items-center gap-1 px-4 py-2 bg-white border-b border-gray-100 overflow-x-auto flex-shrink-0">
          {safePages.map((page, idx) => (
            <div key={page.id} className="flex items-center gap-1 flex-shrink-0">
              <div
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold cursor-pointer transition-colors ${clampedIdx === idx ? "bg-[#004A99] text-white" : "bg-gray-100 text-gray-600 hover:bg-gray-200"}`}
                onClick={() => switchPage(idx)}
              >
                <i className="ri-file-text-line text-xs" />
                <span
                  contentEditable
                  suppressContentEditableWarning
                  onBlur={(e) => renamePage(idx, e.currentTarget.textContent ?? page.name)}
                  onClick={(e) => e.stopPropagation()}
                  className="outline-none min-w-8"
                >
                  {page.name}
                </span>
              </div>
              <div className="flex items-center gap-0.5">
                <button
                  type="button"
                  onClick={() => duplicatePage(idx)}
                  title="Nhân đôi trang"
                  className="w-5 h-5 flex items-center justify-center text-gray-400 hover:text-[#004A99] cursor-pointer rounded"
                >
                  <i className="ri-file-copy-line text-xs" />
                </button>
                {safePages.length > 1 && (
                  <button
                    type="button"
                    onClick={() => deletePage(idx)}
                    title="Xóa trang"
                    className="w-5 h-5 flex items-center justify-center text-gray-400 hover:text-red-500 cursor-pointer rounded"
                  >
                    <i className="ri-close-line text-xs" />
                  </button>
                )}
              </div>
            </div>
          ))}
          <button
            type="button"
            onClick={addPage}
            className="flex items-center gap-1 px-2 py-1.5 text-xs text-gray-500 hover:text-[#004A99] cursor-pointer rounded-lg hover:bg-gray-100 transition-colors whitespace-nowrap flex-shrink-0"
          >
            <i className="ri-add-line" />
            {t("Thêm trang", "페이지 추가")}
          </button>
        </div>

        {/* ── Main area ── */}
        {/* FIX: overflow-hidden removed, use flex with proper height */}
        <div className="flex flex-1" style={{ minHeight: 0, overflow: "hidden" }}>
          {editPanelOpen && selectedBlock ? (
            <div className="w-full h-full overflow-y-auto bg-white px-3 py-2">
              <div className="bg-white border-b border-gray-100 px-2 py-2 flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <i className="ri-edit-box-line text-[#004A99] text-sm" />
                  <p className="text-xs font-bold text-gray-800">{t("Chỉnh sửa block", "블록 편집")}</p>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => setShowPreview(true)}
                    className="flex items-center gap-1.5 px-3 py-1.5 bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-semibold rounded-lg cursor-pointer whitespace-nowrap transition-colors"
                  >
                    <i className="ri-eye-line" />
                    {t("Xem trước", "미리보기")}
                  </button>
                  <button
                    type="button"
                    onClick={() => setEditPanelOpen(false)}
                    className="w-6 h-6 flex items-center justify-center rounded-lg hover:bg-gray-100 cursor-pointer"
                  >
                    <i className="ri-close-line text-gray-500 text-sm" />
                  </button>
                </div>
              </div>
              <BlockEditor
                block={selectedBlock}
                lang={lang}
                onChange={updateBlock}
              />
            </div>
          ) : (
            <div
              ref={canvasRef}
              className="flex-1 p-4 space-y-3"
              style={{ overflowY: "auto", overflowX: "hidden", height: "100%" }}
            >
              {/* Block library overlay */}
              {showLibrary && (
                <div className="bg-white border-2 border-[#004A99]/20 rounded-2xl p-4 mb-4">
                  <div className="flex items-center justify-between mb-3">
                    <p className="text-xs font-bold text-gray-700 uppercase tracking-wider">
                      {t("Thư viện Block — Chọn để thêm vào trang", "블록 라이브러리 — 클릭하여 추가")}
                    </p>
                    <button type="button" onClick={() => setShowLibrary(false)} className="w-6 h-6 flex items-center justify-center rounded-lg hover:bg-gray-100 cursor-pointer">
                      <i className="ri-close-line text-gray-500 text-sm" />
                    </button>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    {BLOCK_LIBRARY.map((item) => (
                      <button
                        key={item.type}
                        type="button"
                        onClick={() => addBlock(item.type)}
                        className={`flex items-center gap-3 p-3 rounded-xl border-2 text-left cursor-pointer hover:scale-[1.02] transition-transform ${item.color}`}
                      >
                        <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-white/60 flex-shrink-0">
                          <i className={`${item.icon} text-base`} />
                        </div>
                        <div className="min-w-0">
                          <p className="text-xs font-bold truncate">{item.label}</p>
                          <p className="text-[10px] opacity-70 truncate">{item.description}</p>
                        </div>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Empty state */}
              {activePage.blocks.length === 0 && !showLibrary && (
                <div className="border-2 border-dashed border-gray-200 rounded-2xl p-12 text-center">
                  <div className="w-16 h-16 bg-[#EDF2FB] rounded-2xl flex items-center justify-center mx-auto mb-4">
                    <i className="ri-layout-masonry-line text-3xl text-[#004A99]" />
                  </div>
                  <p className="text-sm font-bold text-gray-700 mb-1">{t("Trang này chưa có nội dung", "이 페이지에 콘텐츠가 없습니다")}</p>
                  <p className="text-xs text-gray-400 mb-4">{t("Bấm \"Thêm block\" để bắt đầu thiết kế trang sản phẩm", "\"블록 추가\"를 클릭하여 제품 페이지 디자인을 시작하세요")}</p>
                  <button
                    type="button"
                    onClick={() => setShowLibrary(true)}
                    className="flex items-center gap-2 px-4 py-2 bg-[#004A99] text-white text-sm font-semibold rounded-xl cursor-pointer mx-auto whitespace-nowrap"
                  >
                    <i className="ri-add-line" />
                    {t("Thêm block đầu tiên", "첫 번째 블록 추가")}
                  </button>
                </div>
              )}

              {/* Block list */}
              {activePage.blocks.map((block, idx) => (
                <div
                  key={block.id}
                  draggable
                  onDragStart={() => handleDragStart(block.id)}
                  onDragOver={(e) => handleDragOver(e, block.id)}
                  onDrop={handleDrop}
                >
                  <BlockPreview
                    block={block}
                    isSelected={selectedBlockId === block.id}
                    onClick={() => {
                      setSelectedBlockId(block.id);
                      setEditPanelOpen(true);
                      setShowLibrary(false);
                    }}
                    onMoveUp={() => moveBlock(block.id, "up")}
                    onMoveDown={() => moveBlock(block.id, "down")}
                    onDuplicate={() => duplicateBlock(block.id)}
                    onDelete={() => deleteBlock(block.id)}
                    onInlineChange={updateBlock}
                    isFirst={idx === 0}
                    isLast={idx === activePage.blocks.length - 1}
                  />
                </div>
              ))}

              {/* Add block at bottom */}
              {activePage.blocks.length > 0 && (
                <button
                  type="button"
                  onClick={() => { setShowLibrary(true); setEditPanelOpen(false); }}
                  className="w-full flex items-center justify-center gap-2 py-3 border-2 border-dashed border-gray-200 rounded-xl text-sm text-gray-400 hover:border-[#004A99] hover:text-[#004A99] transition-colors cursor-pointer"
                >
                  <i className="ri-add-line" />
                  {t("Thêm block mới", "새 블록 추가")}
                </button>
              )}

              {/* Bottom padding for scroll */}
              <div className="h-8" />
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default VisualBuilder;
